import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import NotifyMeModal from '../../notify-me-modal'

const QuickViewActions = ({
    styles,
    isNotifyMe,
    handleNotifyMe,
    isNotifyMeLoading,
    notifyMeError,
    isNotifyMeModalOpen,
    handleNotifyMeCloseModal,
    product,
    variant,
    notifyMeSuccessContent,
    disableAddToCart,
    cartInventoryMessage,
    handleCartItem,
    shouldShowShopSimilar,
    onShopSimilar,
    cartEditMode = false,
    isUpdating = false,
    updateError = '',
    isVariationDataLoading = false,
    isAvailabilityResolved = true,
    notifyMeDrawerTitle,
    notifyMeRecommendation
}) => {
    const intl = useIntl()

    // One mounted button; only the label/handler/testid swap between modes (no remount flicker).
    let computedMode = 'cart'
    if (isNotifyMe) {
        computedMode = 'notify'
    } else if (shouldShowShopSimilar && !isVariationDataLoading) {
        computedMode = 'shopSimilar'
    }

    // Hold the last label until availability resolves, so the mode swaps exactly once.
    const lastModeRef = useRef(computedMode)
    if (isAvailabilityResolved) {
        lastModeRef.current = computedMode
    }
    const mode = isAvailabilityResolved ? computedMode : lastModeRef.current

    const cartLabel = cartEditMode
        ? intl.formatMessage({defaultMessage: 'Update', id: 'cart_edit.button.update'})
        : intl.formatMessage({defaultMessage: 'Add to Bag', id: 'product_view.button.add_to_cart'})

    // When add-to-cart isn't valid, drop the click instead of disabling (keeps it black).
    const inert = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }
    const cartClick = disableAddToCart
        ? inert
        : (e) => {
              e.preventDefault()
              e.stopPropagation()
              handleCartItem()
          }

    const actionByMode = {
        notify: {
            testId: 'qv-button-notify-me',
            label: <FormattedMessage defaultMessage="Notify Me" id="pdp_notify_me_cta.label" />,
            onClick: handleNotifyMe,
            isLoading: isNotifyMeLoading
        },
        shopSimilar: {
            testId: 'qv-button-shop-similar',
            label: intl.formatMessage({
                defaultMessage: 'Shop similar',
                id: 'product_view.button.shop_similar'
            }),
            onClick: onShopSimilar,
            isLoading: false
        },
        cart: {
            testId: 'qv-button-add-to-cart',
            label: cartLabel,
            onClick: cartClick,
            isLoading: isUpdating
        }
    }
    const action = actionByMode[mode]

    return (
        <Box {...styles.actionsContainer}>
            {mode === 'cart' && (cartInventoryMessage || updateError) && (
                <Box {...styles.maxQtyErrorText} role="alert" marginBottom={2}>
                    {cartInventoryMessage || updateError}
                </Box>
            )}
            <Button
                key="qv-action-button"
                onClick={action.onClick}
                isLoading={action.isLoading}
                variant={'webPrimary'}
                w={'100%'}
                data-testid={action.testId}
            >
                {action.label}
            </Button>
            {mode === 'notify' && notifyMeError && (
                <Text {...styles.notifyMeError}>{notifyMeError}</Text>
            )}
            {mode === 'notify' && (
                <NotifyMeModal
                    key="notify-me-modal"
                    isOpen={isNotifyMeModalOpen}
                    onClose={handleNotifyMeCloseModal}
                    product={product}
                    variant={variant}
                    showSuccessContent={notifyMeSuccessContent}
                    notifyMeDrawerTitle={notifyMeDrawerTitle}
                    notifyMeRecommendation={notifyMeRecommendation}
                />
            )}
        </Box>
    )
}

QuickViewActions.propTypes = {
    styles: PropTypes.object,
    isNotifyMe: PropTypes.bool,
    handleNotifyMe: PropTypes.func,
    isNotifyMeLoading: PropTypes.bool,
    notifyMeError: PropTypes.string,
    isNotifyMeModalOpen: PropTypes.bool,
    handleNotifyMeCloseModal: PropTypes.func,
    product: PropTypes.object,
    variant: PropTypes.object,
    notifyMeSuccessContent: PropTypes.bool,
    disableAddToCart: PropTypes.bool,
    cartInventoryMessage: PropTypes.string,
    handleCartItem: PropTypes.func,
    shouldShowShopSimilar: PropTypes.bool,
    onShopSimilar: PropTypes.func,
    cartEditMode: PropTypes.bool,
    isUpdating: PropTypes.bool,
    updateError: PropTypes.string,
    isVariationDataLoading: PropTypes.bool,
    isAvailabilityResolved: PropTypes.bool,
    notifyMeDrawerTitle: PropTypes.object,
    notifyMeRecommendation: PropTypes.object
}

export default QuickViewActions
